import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import { useUndoOptional } from "@/lib/undo/UndoContext";
import type {
  DataResponse,
  SlotEdgeTable,
  SlotZmenaHranRequest,
  SlotZmenaHranResponse,
} from "@/api/schemas";

/** Tabuľka hrán per hrúbka dielca pre slot (auto + override merge). */
export function useSlotEdgesByThickness(
  slotId: string | undefined,
  extraHrubky?: number[],
): UseQueryResult<DataResponse<SlotEdgeTable>> {
  const csv = extraHrubky?.length ? extraHrubky.join(",") : undefined;
  return useQuery({
    queryKey: queryKeys.slotEdges.byThickness(slotId ?? "", csv),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<SlotEdgeTable>>(
        `/sloty/${slotId}/edges-by-thickness`,
        { params: csv ? { hrubky: csv } : undefined },
      );
      return data;
    },
    enabled: !!slotId,
  });
}

/**
 * Tá istá tabuľka pre materiál, ktorý sa do slotu ešte len vyberá
 * (KOLO 135) — „!" pri chýbajúcej hrúbke hneď po výbere materiálu.
 */
export function useHranyPodlaHrubky(
  materialId: string | undefined,
): UseQueryResult<DataResponse<SlotEdgeTable>> {
  return useQuery({
    queryKey: [...queryKeys.slotEdges.all, "material", materialId ?? ""] as const,
    queryFn: async () => {
      const { data } = await api.get<DataResponse<SlotEdgeTable>>(
        `/cennik/hrany-podla-hrubky/${materialId}`,
      );
      return data;
    },
    enabled: !!materialId,
  });
}

/** Pridaj / vylúč hranu zo zoznamu detekovaných hrán pre slot. */
export function useSlotEdgeCuration(
  slotId: string,
): UseMutationResult<
  void,
  Error,
  { materialId: string; mode: "add" | "exclude" }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ materialId, mode }) => {
      await api.post(`/sloty/${slotId}/edge-curation`, {
        material_id: materialId,
        mode,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.cennik.all });
      void qc.invalidateQueries({ queryKey: queryKeys.slotEdges.all });
      // Kurácia kandidátov mení auto-priradené hrany → ceny dielcov
      void qc.invalidateQueries({ queryKey: queryKeys.zakazky.all });
      void qc.invalidateQueries({
        queryKey: queryKeys.varianty.edgesSummaries(),
      });
    },
  });
}

/**
 * Zmena hrán materiálu zákazky, ktorá sa prenesie do dielcov (KOLO 222).
 *
 * User: *„výber hrán v materiálovom slote by sa mal správať ako globálne
 * nastavenie… keď zmeníš, vypíše, že sa menia"*. Ten istý endpoint robí
 * náhľad (`nahlad: true` — server beh vráti, nič nezapíše) aj zápis;
 * zápis je **jeden krok späť** (Ctrl+Z) za tabuľku, kuráciu, dosku
 * aj všetky zmenené dielce.
 */
export function useZmenaHranSlotu(
  slotId: string,
): UseMutationResult<
  SlotZmenaHranResponse,
  Error,
  { telo: SlotZmenaHranRequest; popis: string }
> {
  const qc = useQueryClient();
  const undo = useUndoOptional();
  const obnov = async (): Promise<void> => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: queryKeys.slotEdges.all }),
      qc.invalidateQueries({ queryKey: queryKeys.cennik.all }),
      // Hrany dielcov, ich ceny, materiál slotu aj sumár hrán vo variante
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.all }),
      qc.invalidateQueries({ queryKey: queryKeys.varianty.edgesSummaries() }),
    ]);
  };
  return useMutation({
    mutationFn: async ({ telo }) => {
      const { data } = await api.post<DataResponse<SlotZmenaHranResponse>>(
        `/sloty/${slotId}/zmena-hran`,
        telo,
      );
      return data.data;
    },
    onSuccess: (vysledok, { popis }) => {
      if (!vysledok.ulozene) return;
      void obnov();
      const predtym = vysledok.predtym;
      if (!predtym) return;
      undo?.pridaj({
        popis,
        vrat: async () => {
          await api.post(`/sloty/${slotId}/zmena-hran/vratit`, { predtym });
          await obnov();
        },
      });
    },
  });
}
